import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Divider, Alert, Grid, TextField, MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AddIcon from '@mui/icons-material/Add';

import * as inventoryApi from '../../../lib/api/inventory';

/**
 * 생산 완료분을 내보내는 두 가지 — 창고에 넣기와 즉시 출고.
 *
 * 둘은 받는 것이 같다. 제품 · 수량 · 창고 · 비고.
 * 즉시 출고도 "출고용 창고에 넣고 바로 내보내는" 것이라 창고를 거치기 때문이다.
 * 받는 것이 같은데 화면을 둘로 두면 한쪽만 고쳐진다. 문구만 갈라 쓴다.
 *
 * 창고는 사람이 고른다 — 자재를 쌓아두는 곳과 나갈 물건을 두는 곳이 다르다.
 * 다만 매번 고르는 손을 줄이려고 기본 창고를 미리 채워둔다.
 */
const TEXT = {
  WAREHOUSE_IN: {
    title: '창고 입고',
    submit: '입고',
    submitting: '넣는 중...',
    quantityLabel: '입고 수량',
    warehouseLabel: '입고할 창고',
  },
  SHIP_NOW: {
    title: '즉시 출고',
    submit: '출고',
    submitting: '내보내는 중...',
    quantityLabel: '출고 수량',
    warehouseLabel: '거쳐 갈 창고',
  },
};

const ProductionOutDialog = ({
  open, mode = 'WAREHOUSE_IN', planData, maxQuantity = 0,
  saving, saveError, onClose, onSave,
}) => {
  const text = TEXT[mode] || TEXT.WAREHOUSE_IN;
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [whUuid, setWhUuid] = useState('');
  const [quantity, setQuantity] = useState('');
  const [remarks, setRemarks] = useState('');

  // 출고용 창고가 하나도 없을 때 여기서 바로 만든다.
  // 창고관리로 보내면 하던 일을 놓치고, 돌아와서 다시 시작해야 한다.
  const [newWhNm, setNewWhNm] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  /**
   * 고를 수 있는 창고를 읽어온다.
   *
   * 출고용으로 표시한 창고만 대상이다. 생산품이 자재 창고에 들어가면
   * 출고 화면에 안 나와서, 만들어놓고 못 내보내는 상태가 된다.
   *
   * @param {string} [selectUuid] 방금 만든 창고를 바로 고르고 싶을 때
   */
  const load = useCallback(async (selectUuid) => {
    setLoading(true);
    try {
      const res = await inventoryApi.getWarehouseList({ shipYn: 'Y' });
      const list = res.data?.list || [];
      setWarehouses(list);

      if (selectUuid && list.some((w) => w.whUuid === selectUuid)) {
        setWhUuid(selectUuid);
        return;
      }
      // 기본값: 출고용이면서 기본 창고인 것. 없으면 첫 번째.
      const preferred = list.find((w) => w.isDefault === 'Y') || list[0];
      setWhUuid(preferred ? preferred.whUuid : '');
    } catch (err) {
      setWarehouses([]);
      setWhUuid('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (open) {
      // 대개 남은 것을 전부 넣는다. 고칠 수 있게만 해둔다.
      setQuantity(maxQuantity > 0 ? String(maxQuantity) : '');
      setRemarks('');
      setNewWhNm('');
      setCreateError(null);
    }
  }, [open, maxQuantity]);

  const noWarehouse = !loading && warehouses.length === 0;
  const qty = Number(quantity);
  const qtyInvalid = !quantity || Number.isNaN(qty) || qty <= 0 || qty > maxQuantity;

  const handleSave = () => {
    onSave({ whUuid, quantity: qty, remarks: remarks.trim() || null });
  };

  /** 출고용 창고를 여기서 만든다. 만들자마자 골라진 상태가 된다. */
  const handleCreateWarehouse = async () => {
    const name = newWhNm.trim();
    if (!name) {
      setCreateError('창고 이름을 입력하세요.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const res = await inventoryApi.addWarehouse({
        whNm: name,
        shipYn: 'Y',   // 여기서 만드는 창고는 출고용이다. 그러려고 만드는 것이다.
        isDefault: 'N',
      });
      const created = res.data?.info || res.data;
      await load(created?.whUuid);
      setNewWhNm('');
    } catch (err) {
      setCreateError(err?.response?.data?.message || '창고를 만들지 못했습니다.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarehouseIcon color="primary" />
          <Typography variant="h6">{text.title}</Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* 출고용 창고가 없으면 여기서 만든다.
            "창고관리에서 체크하세요" 로 끝내면 하던 일을 놓치고 다시 시작해야 한다. */}
        {noWarehouse && (
          <Box
            sx={{
              mb: 2, p: 2, borderRadius: 1,
              border: '1px solid', borderColor: 'warning.main',
              backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
              display: 'flex', flexDirection: 'column', gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                출고할 수 있는 창고가 없습니다
              </Typography>
              <Typography variant="body2" color="text.secondary">
                생산품은 출고용 창고에만 넣을 수 있습니다. 여기서 하나 만들면 바로 쓸 수 있습니다.
              </Typography>
            </Box>

            {createError && <Alert severity="error">{createError}</Alert>}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small" fullWidth label="새 창고 이름"
                value={newWhNm}
                onChange={(e) => setNewWhNm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateWarehouse(); }}
                placeholder="예: 출고 대기 창고"
                disabled={creating}
              />
              <Button
                variant="contained" color="warning" startIcon={<AddIcon />}
                onClick={handleCreateWarehouse}
                disabled={creating || !newWhNm.trim()}
                sx={{ whiteSpace: 'nowrap', mt: 0 }}
              >
                {creating ? '만드는 중...' : '창고 만들기'}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary">
              출고용으로 만들어집니다. 주소·분류 같은 나머지 항목은 창고관리에서 채우시면 됩니다.
              이미 쓰는 창고가 있다면 창고관리에서 그 창고를 &quot;출고용 창고&quot;로 체크해도 됩니다.
            </Typography>
          </Box>
        )}
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth size="small" label="제품"
              value={planData?.productName || '-'}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" type="number" label={text.quantityLabel}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ min: 1, max: maxQuantity }}
              error={Boolean(quantity) && qtyInvalid}
              helperText={`남은 수량 ${maxQuantity}개`}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select fullWidth size="small" label={text.warehouseLabel}
              value={whUuid}
              onChange={(e) => setWhUuid(e.target.value)}
              disabled={noWarehouse}
              helperText={
                mode === 'SHIP_NOW'
                  ? '창고에 넣고 바로 내보냅니다. 이력에는 입고와 출고가 모두 남습니다.'
                  : '출고용으로 표시한 창고만 나옵니다.'
              }
            >
              {warehouses.map((w) => (
                <MenuItem key={w.whUuid} value={w.whUuid}>
                  {w.whNm}{w.isDefault === 'Y' ? ' (기본)' : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="비고"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="선택"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>취소</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || noWarehouse || !whUuid || qtyInvalid}
        >
          {saving ? text.submitting : text.submit}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductionOutDialog;
